/**
 * Client-side attachment limits + helpers. These mirror the server limits for
 * UX only — the server (which sniffs bytes) is authoritative.
 */
import { attachmentFiles } from '@/helpers/ApiHelper';

export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const MAX_ATTACHMENTS_PER_SESSION = 10;
export const IMAGE_ATTACHMENT_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'] as const;
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.pdf',
  ...IMAGE_ATTACHMENT_EXTENSIONS,
  '.docx',
  '.txt',
  '.md',
  '.csv',
  '.json',
] as const;

export const ATTACHMENT_ACCEPT = ALLOWED_ATTACHMENT_EXTENSIONS.join(',');

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Size for limit copy: whole numbers have no trailing ".0" ("10 MB", not "10.0 MB"). */
export function formatLimitBytes(bytes: number): string {
  return formatBytes(bytes).replace(/\.0 /, ' ');
}

/**
 * Whether retrying an upload could help: network errors / timeouts (status 0)
 * and 5xx. 4xx rejections (400/403/404/409/413/415) are deterministic.
 */
export function isTransientUploadFailure(status: number): boolean {
  return status === 0 || status >= 500;
}

export function extensionOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i < 0 ? '' : name.slice(i).toLowerCase();
}

export interface ValidationResult {
  accepted: File[];
  /** One human-readable message per rejected file. */
  errors: string[];
}

/** Validate candidate files against type, size and the per-request count cap. */
export function validateAttachmentFiles(files: File[], existingCount: number): ValidationResult {
  const accepted: File[] = [];
  const errors: string[] = [];
  for (const file of files) {
    const ext = extensionOf(file.name);
    if (!(ALLOWED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)) {
      errors.push(`${file.name}: unsupported file type`);
    } else if (file.size === 0) {
      errors.push(`${file.name}: file is empty`);
    } else if (file.size > MAX_ATTACHMENT_BYTES) {
      errors.push(`${file.name}: larger than ${formatLimitBytes(MAX_ATTACHMENT_BYTES)}`);
    } else if (existingCount + accepted.length >= MAX_ATTACHMENTS_PER_SESSION) {
      errors.push(`${file.name}: limit of ${MAX_ATTACHMENTS_PER_SESSION} files per request reached`);
    } else {
      accepted.push(file);
    }
  }
  return { accepted, errors };
}

/** Fetch an attachment with auth and hand it to the browser as a save. */
export async function downloadAttachment(attachmentId: string, filename: string): Promise<void> {
  const blob = await attachmentFiles.download(attachmentId, false);
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoke after the click has been dispatched.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}
